
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  signal,
  viewChild,
  viewChildren,
  ChangeDetectionStrategy
} from '@angular/core';

import {
    animate,
    animateChild,
    group,
    query,
    state,
    style,
    transition,
    trigger,
} from '@angular/animations';
import { OptionItem, StepContent } from 'matchmaking/models';
import {
    MatchmakingApiService,
    MatchmakingContentService,
    MatchmakingDataService,
    MatchmakingTranslateService,
    TrackingMatchmakingSnowplowService,
} from 'matchmaking/services';
import { FormBackButtonComponent } from '../form-back-button/form-back-button.component';
import { FormContentComponent } from '../form-content/form-content.component';
import { FormOptionListComponent } from '../form-option-list/form-option-list.component';
import { FormOptionComponent } from '../form-option/form-option.component';
import { FormShowMoreLessButtonComponent } from '../form-show-more-less-button/form-show-more-less-button.component';
import { FormSubmitButtonComponent } from '../form-submit-button/form-submit-button.component';

const VISIBLE_OPTIONS = 3;
const ANIMATION_DURATION = 500;

@Component({
    selector: 'matchmaking-step-application',
    imports: [
    FormContentComponent,
    FormOptionComponent,
    FormSubmitButtonComponent,
    FormOptionListComponent,
    FormBackButtonComponent,
    FormShowMoreLessButtonComponent
],
    templateUrl: './step-application.component.html',
    styleUrl: './step-application.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    animations: [
        trigger('expandCollapse', [
            transition('true <=> false', [
                group([
                    query('@childAnimation', animateChild(), { optional: true }),
                    animate(ANIMATION_DURATION + 'ms'),
                ]),
            ]),
        ]),
        trigger('childAnimation', [
            state('true', style({
                maxHeight: '{{maxHeight}}px',
            }), { params: { maxHeight: 0 } }),
            state('false', style({
                maxHeight: '0px',
            })),
            transition('false <=> true', [animate(ANIMATION_DURATION + 'ms')]),
        ]),
    ]
})
export class StepApplicationComponent implements OnInit {
    translateService = inject(MatchmakingTranslateService);
    dataService = inject(MatchmakingDataService);
    apiService = inject(MatchmakingApiService);
    contentService = inject(MatchmakingContentService);
    trackingService = inject(TrackingMatchmakingSnowplowService);

    content = input.required<StepContent>();

    formOptionElementRef = viewChild<ElementRef<HTMLTextAreaElement>>('formOptionElement');
    extendedOptionsRef = viewChildren<HTMLButtonElement, ElementRef<HTMLButtonElement>>(
        'extendedOptions',
        { read: ElementRef },
    );

    showAllOptions = signal(false);

    translations = computed(() => this.translateService.translations());
    selectedOption = computed(() => this.dataService.selectedApplicationTitle());
    shouldShowShowMoreButton = computed(
        () => this.contentService.getApplications().length > VISIBLE_OPTIONS,
    );
    visibleOptions = computed(() =>
        this.contentService.getApplications().filter((item, index) => index < VISIBLE_OPTIONS),
    );
    hiddenOptions = computed(() =>
        this.contentService.getApplications().filter((item, index) => index >= VISIBLE_OPTIONS),
    );

    constructor() {
        let isFirstChange = true;

        effect(() => {
            const currentState = this.showAllOptions();
            if (isFirstChange) {
                isFirstChange = false;
                return;
            }

            this.trackingService.trackButtonClick({
                identifier: 'step_2_show_more',
                element_content: currentState ? 'open' : 'close',
            });
        });
    }

    ngOnInit() {
        const config = this.dataService.config();

        if (config) {
            this.trackingService.trackStepStart({ step_number: 2, step_name: config.currentStep });
        }
    }

    getAnimation() {
        let maxHeight = 0;

        if (this.extendedOptionsRef() && this.extendedOptionsRef()?.length > 0) {
            maxHeight = this.extendedOptionsRef().reduce(
                (total, item) => total + item.nativeElement?.offsetHeight,
                0,
            );
        }

        return {
            value: this.showAllOptions(),
            params: {
                maxHeight: maxHeight,
            },
        };
    }

    setSelected(option: OptionItem) {
        this.dataService.updateConfig({
            selectedApplication: this.dataService.getApplicationById(option.title),
        });
    }

    submit() {
        this.apiService.submit();
    }

    handleBack() {
        this.apiService.goToPreviousStep();
    }

    toggleShowAllOptions(showAll: boolean) {
        this.showAllOptions.set(showAll);

        if (!showAll) {
            setTimeout(() => {
                this.formOptionElementRef()?.nativeElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                });
            }, ANIMATION_DURATION);
        }
    }
}
